// File: VendorRanking.js

import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Card, CardContent } from '@mui/material';
import { CISCO_CRITERIA } from '../Data/Criteria_Cisco';
import { PALO_ALTO_CRITERIA } from '../Data/Criteria_Palo_Alto';
import { ARUBA_CRITERIA } from '../Data/Criteria_Aruba';
import { JUNIPER_CRITERIA } from '../Data/Criteria_Juniper';
import { AIRESPRING_CRITERIA } from '../Data/Criteria_Airespring';

// Vendor data map
const VENDOR_DATA_MAP = {
  CISCO: CISCO_CRITERIA.CISCO,
  PALO_ALTO: PALO_ALTO_CRITERIA.PALO_ALTO,
  ARUBA: ARUBA_CRITERIA.ARUBA,
  JUNIPER: JUNIPER_CRITERIA.JUNIPER,
  AIRESPRING: AIRESPRING_CRITERIA.AIRESPRING,
};

// Utility function to calculate percentage scores
const calculatePercentageScore = (vendorData = [], criterion) => {
  const relevantCriteria = vendorData.filter((item) => item.Criteria === criterion);
  const yesCount = relevantCriteria.filter((item) => item.Check === 'Yes').length;
  const totalCount = relevantCriteria.length;
  const percentage = totalCount > 0 ? (yesCount / totalCount) * 100 : 0;

  return { percentage, yesCount, totalCount };
};

// Calculate scores based on price
const calculatePriceScore = (price) => {
  if (price <= 2_000_000) return 1;
  if (price <= 4_000_000) return 2;
  if (price <= 8_000_000) return 3;
  return 4;
};

const calculateScoreRange = (percentage) => {
  if (percentage <= 25) return 1;
  if (percentage <= 50) return 2;
  if (percentage <= 75) return 3;
  return 4;
};

const calculateOverallScore = (priceScore, techScore, opScore) => {
  return (priceScore + techScore + opScore) / 3;
};

export const VendorRanking = ({ vendors = [] }) => {
  const [vendorRanks, setVendorRanks] = useState([]);

  useEffect(() => {
    const processedRanks = vendors.map((vendor) => {
      const name = vendor.name || 'Unknown Vendor';
      const price = vendor.price || 0;
      const vendorData = VENDOR_DATA_MAP[name] || [];

      const techData = calculatePercentageScore(vendorData, 'Technology');
      const opData = calculatePercentageScore(vendorData, 'Operational');

      const priceScore = calculatePriceScore(price);
      const techScore = calculateScoreRange(techData.percentage);
      const opScore = calculateScoreRange(opData.percentage);
      const overallScore = calculateOverallScore(priceScore, techScore, opScore);

      return {
        name,
        priceScore,
        techScore,
        opScore,
        overallScore,
        details: {
          price,
          techPercentage: techData.percentage || 0,
          opPercentage: opData.percentage || 0,
        },
      };
    });

    setVendorRanks(processedRanks);
  }, [vendors]);

  return (
    <Paper
      sx={{
        p: 3,
        mb: 2,
        backgroundColor: 'white',
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
        Vendor Ranking
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {vendorRanks.map((vendor) => (
          <Card
            key={vendor.name}
            sx={{
              width: '300px',
              border: 1,
              borderColor: 'grey.300',
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                {vendor.name}
              </Typography>
              <Typography>Price: ${vendor.details.price.toLocaleString()}</Typography>
              <Typography>Price Score: {vendor.priceScore}</Typography>
              <Typography>
                Tech Score: {vendor.techScore} ({vendor.details.techPercentage.toFixed(2)}%)
              </Typography>
              <Typography>
                Operational Score: {vendor.opScore} ({vendor.details.opPercentage.toFixed(2)}%)
              </Typography>
              <Typography sx={{ mt: 1, fontWeight: 'bold' }}>
                Overall Score: {vendor.overallScore.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Paper>
  );
};
